import { BusinessObjectBase } from './base';
import { processServiceSchema } from '@schemas/business-objects/process-service';
import type { ProcessServiceProperty } from './process-service-property';
import type { Id } from '../base-types';

export interface ProcessServiceData {
  readonly id: Id;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly properties?: ProcessServiceProperty[];
}

export interface ProcessService extends Readonly<ProcessServiceData> {}
type ProcessServiceCreateWithChildrenData = Partial<
  Omit<ProcessServiceData, 'properties'>
> & { properties?: Partial<ProcessServiceProperty>[] };

export class ProcessService extends BusinessObjectBase {
  static readonly entityName = 'ProcessService' as const;
  static readonly plural = 'process-services';
  static schema = processServiceSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessService[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessService(data));
  }

  static async findOne(filter?: any): Promise<ProcessService | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessService(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessService> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessService(data);
  }

  static async create(
    data: Omit<ProcessServiceData, 'id'>,
  ): Promise<ProcessService> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessService(result);
  }

  static async createMany(
    data: Omit<ProcessServiceData, 'id'>[],
  ): Promise<ProcessService[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessService(item));
  }

  static async createWithChildren(
    data: ProcessServiceCreateWithChildrenData,
  ): Promise<ProcessService> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessService(result);
  }

  static async createManyWithChildren(
    data: ProcessServiceCreateWithChildrenData[],
  ): Promise<ProcessService[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessService(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessServiceData>,
  ): Promise<ProcessService> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessService(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
